import React, { useState } from 'react';
import { 
  Search, Filter, Download, ChevronLeft, ChevronRight, 
  Plus, Edit, Trash2, Eye, Calendar, Percent, Tag,
  Clock, CheckCircle, XCircle, AlertCircle, Gift,
  ShoppingBag, Users, TrendingUp, Zap, Flame,
  Award, Crown, Sparkles, Rocket
} from 'lucide-react';

const Offers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const itemsPerPage = 5;

  // Static Mock Data
  const [offers, setOffers] = useState([
    {
      id: 'OFR-001',
      title: 'Summer Sale Extravaganza',
      description: 'Get upto 50% off on all indoor plants',
      type: 'seasonal',
      discountType: 'percentage',
      discountValue: 50,
      startDate: 'Jun 1, 2024',
      endDate: 'Jun 30, 2024',
      status: 'active',
      priority: 'high',
      applicableProducts: ['All Indoor Plants'],
      image: '☀️',
      usedCount: 1245,
      totalRedemptions: 5000,
      bannerBg: '#fef3c7'
    },
    {
      id: 'OFR-002',
      title: 'Buy 1 Get 1 Free',
      description: 'Buy any flowering plant, get one free',
      type: 'bogo',
      discountType: 'bogo',
      discountValue: 100,
      startDate: 'Jul 1, 2024',
      endDate: 'Jul 31, 2024',
      status: 'active',
      priority: 'high',
      applicableProducts: ['Flowering Plants'],
      image: '🌸',
      usedCount: 892,
      totalRedemptions: 2000,
      bannerBg: '#dbeafe'
    },
    {
      id: 'OFR-003',
      title: 'Monsoon Magic',
      description: 'Free fertilizer with every plant purchase',
      type: 'combo',
      discountType: 'free_gift',
      discountValue: 0,
      startDate: 'Jul 15, 2024',
      endDate: 'Aug 15, 2024',
      status: 'upcoming',
      priority: 'medium',
      applicableProducts: ['All Plants'],
      image: '🌧️',
      usedCount: 0,
      totalRedemptions: 3000,
      bannerBg: '#e9d5ff'
    },
    {
      id: 'OFR-004',
      title: 'First Time Buyer',
      description: '15% off on your first order',
      type: 'welcome',
      discountType: 'percentage',
      discountValue: 15,
      startDate: 'Jan 1, 2024',
      endDate: 'Dec 31, 2024',
      status: 'active',
      priority: 'low',
      applicableProducts: ['All Products'],
      image: '🎁',
      usedCount: 3421,
      totalRedemptions: 10000,
      bannerBg: '#d1fae5'
    },
    {
      id: 'OFR-005',
      title: 'Weekend Flash Sale',
      description: 'Extra 10% off on orders above ₹999',
      type: 'flash',
      discountType: 'percentage',
      discountValue: 10,
      startDate: 'Apr 5, 2024',
      endDate: 'Apr 7, 2024',
      status: 'expired',
      priority: 'high',
      applicableProducts: ['All Products'],
      image: '⚡',
      usedCount: 567,
      totalRedemptions: 1000,
      bannerBg: '#fee2e2'
    }
  ]);

  const stats = {
    total: offers.length,
    active: offers.filter(o => o.status === 'active').length,
    upcoming: offers.filter(o => o.status === 'upcoming').length,
    expired: offers.filter(o => o.status === 'expired').length,
    totalRedemptions: offers.reduce((sum, o) => sum + o.usedCount, 0)
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'seasonal': return { label: 'Seasonal', icon: Calendar, color: '#10b981', bg: '#d1fae5' };
      case 'bogo': return { label: 'BOGO', icon: Gift, color: '#3b82f6', bg: '#dbeafe' };
      case 'flash': return { label: 'Flash Sale', icon: Zap, color: '#f59e0b', bg: '#fed7aa' };
      case 'welcome': return { label: 'Welcome', icon: Crown, color: '#8b5cf6', bg: '#e9d5ff' };
      case 'combo': return { label: 'Combo', icon: Sparkles, color: '#06b6d4', bg: '#cffafe' };
      default: return { label: type, icon: Tag, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'active': return { label: 'Active', icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'upcoming': return { label: 'Upcoming', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      case 'expired': return { label: 'Expired', icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      default: return { label: status, icon: AlertCircle, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high': return { label: 'High', icon: Flame, color: '#ef4444', bg: '#fee2e2' };
      case 'medium': return { label: 'Medium', icon: TrendingUp, color: '#f59e0b', bg: '#fed7aa' };
      default: return { label: 'Low', icon: Award, color: '#10b981', bg: '#d1fae5' };
    }
  };

  const getDiscountDisplay = (offer) => {
    switch (offer.discountType) {
      case 'percentage': return `${offer.discountValue}% OFF`;
      case 'bogo': return 'Buy 1 Get 1 Free';
      case 'free_gift': return 'Free Gift';
      default: return `${offer.discountValue}% OFF`;
    }
  };

  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          offer.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || offer.type === filterType;
    const matchesStatus = filterStatus === 'all' || offer.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const totalPages = Math.ceil(filteredOffers.length / itemsPerPage);
  const paginatedOffers = filteredOffers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{typeof value === 'number' ? value.toLocaleString() : value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const handleDelete = (offerId) => {
    if (window.confirm('Are you sure you want to delete this offer?')) {
      setOffers(offers.filter(o => o.id !== offerId));
      alert('Offer deleted successfully!');
    }
  };

  const getOfferIcon = (type) => {
    switch (type) {
      case 'seasonal': return '🌿';
      case 'bogo': return '🎁';
      case 'flash': return '⚡';
      case 'welcome': return '👋';
      case 'combo': return '✨';
      default: return '🏷️';
    }
  };

  return (
    <div className="admin-offers-modern">
      {/* Header */}
      <div className="offers-header">
        <div>
          <h1 className="page-title">Special Offers</h1>
          <p className="page-subtitle">Manage promotional offers and discounts</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            Create Offer
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Offers" value={stats.total} icon={Tag} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Active" value={stats.active} icon={CheckCircle} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Upcoming" value={stats.upcoming} icon={Clock} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Total Redemptions" value={stats.totalRedemptions} icon={ShoppingBag} color="#8b5cf6" bgColor="#e9d5ff" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by title or description..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="seasonal">Seasonal</option>
            <option value="bogo">BOGO</option>
            <option value="flash">Flash Sale</option>
            <option value="welcome">Welcome</option>
            <option value="combo">Combo</option>
          </select>
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="upcoming">Upcoming</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredOffers.length} Offers Found</span>
      </div>

      {/* Offers Grid */}
      <div className="offers-grid">
        {paginatedOffers.map((offer) => {
          const typeConfig = getTypeConfig(offer.type);
          const statusConfig = getStatusConfig(offer.status);
          const priorityConfig = getPriorityConfig(offer.priority);
          const TypeIcon = typeConfig.icon;
          const StatusIcon = statusConfig.icon;
          
          return (
            <div key={offer.id} className="offer-card" style={{ background: offer.bannerBg }}>
              <div className="offer-card-header">
                <div className="offer-icon">{offer.image}</div>
                <div className="offer-type-badge" style={{ backgroundColor: typeConfig.bg, color: typeConfig.color }}>
                  <TypeIcon size={12} />
                  {typeConfig.label}
                </div>
              </div>
              <div className="offer-card-body">
                <h3 className="offer-title">{offer.title}</h3>
                <p className="offer-description">{offer.description}</p>
                <div className="offer-discount">
                  <span className="discount-badge">{getDiscountDisplay(offer)}</span>
                  <span className="priority-badge" style={{ backgroundColor: priorityConfig.bg, color: priorityConfig.color }}>
                    {priorityConfig.label} Priority
                  </span>
                </div>
                <div className="offer-dates">
                  <div className="date-item">
                    <Calendar size={12} />
                    <span>{offer.startDate}</span>
                  </div>
                  <span className="date-arrow">→</span>
                  <div className="date-item">
                    <Calendar size={12} />
                    <span>{offer.endDate}</span>
                  </div>
                </div>
                <div className="offer-stats">
                  <div className="stat-item">
                    <ShoppingBag size={12} />
                    <span>{offer.usedCount} / {offer.totalRedemptions}</span>
                  </div>
                  <div className="usage-bar">
                    <div className="usage-fill" style={{ width: `${(offer.usedCount / offer.totalRedemptions) * 100}%` }}></div>
                  </div>
                </div>
                <div className="offer-status">
                  <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                    <StatusIcon size={12} />
                    {statusConfig.label}
                  </span>
                </div>
              </div>
              <div className="offer-card-footer">
                <button className="action-btn edit" onClick={() => {
                  setSelectedOffer(offer);
                  setShowEditModal(true);
                }} title="Edit Offer">
                  <Edit size={16} />
                </button>
                <button className="action-btn delete" onClick={() => handleDelete(offer.id)} title="Delete Offer">
                  <Trash2 size={16} />
                </button>
                <button className="action-btn view" title="View Details">
                  <Eye size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredOffers.length)} of {filteredOffers.length} offers
          </div>
          <div className="pagination-buttons">
            <button 
              className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button 
                key={i}
                className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Offer Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedOffer(null);
        }}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showCreateModal ? 'Create New Offer' : 'Edit Offer'}</h3>
              <button className="modal-close" onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedOffer(null);
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Offer Title</label>
                <input type="text" className="form-input" placeholder="e.g., Summer Sale" defaultValue={selectedOffer?.title || ''} />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea className="form-textarea" rows="2" placeholder="Brief description" defaultValue={selectedOffer?.description || ''} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Offer Type</label>
                  <select className="form-select" defaultValue={selectedOffer?.type || 'seasonal'}>
                    <option value="seasonal">Seasonal</option>
                    <option value="bogo">BOGO (Buy One Get One)</option>
                    <option value="flash">Flash Sale</option>
                    <option value="welcome">Welcome</option>
                    <option value="combo">Combo</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Discount Type</label>
                  <select className="form-select" defaultValue={selectedOffer?.discountType || 'percentage'}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="bogo">BOGO</option>
                    <option value="free_gift">Free Gift</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Discount Value</label>
                  <input type="number" className="form-input" placeholder="e.g., 20" defaultValue={selectedOffer?.discountValue || ''} />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select className="form-select" defaultValue={selectedOffer?.priority || 'medium'}>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" className="form-input" defaultValue={selectedOffer?.startDate || ''} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" className="form-input" defaultValue={selectedOffer?.endDate || ''} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Total Redemptions Limit</label>
                  <input type="number" className="form-input" placeholder="Total limit" defaultValue={selectedOffer?.totalRedemptions || 5000} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-select" defaultValue={selectedOffer?.status || 'active'}>
                    <option value="active">Active</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Applicable Products/Categories</label>
                <select className="form-select" multiple size="3">
                  <option>All Products</option>
                  <option>Indoor Plants</option>
                  <option>Outdoor Plants</option>
                  <option>Flowering Plants</option>
                  <option>Air Purifying Plants</option>
                  <option>Succulents</option>
                  <option>Garden Tools</option>
                  <option>Pots & Planters</option>
                </select>
                <small className="form-hint">Hold Ctrl/Cmd to select multiple</small>
              </div>
              <div className="form-group">
                <label>Banner Color</label>
                <select className="form-select" defaultValue={selectedOffer?.bannerBg || '#fef3c7'}>
                  <option value="#fef3c7">Yellow</option>
                  <option value="#dbeafe">Blue</option>
                  <option value="#d1fae5">Green</option>
                  <option value="#e9d5ff">Purple</option>
                  <option value="#fee2e2">Red</option>
                  <option value="#cffafe">Cyan</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedOffer(null);
              }}>Cancel</button>
              <button className="btn-primary" onClick={() => {
                alert(showCreateModal ? 'Offer created!' : 'Offer updated!');
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedOffer(null);
              }}>{showCreateModal ? 'Create Offer' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default Offers;